import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Eyebrow, MaskedLines, Reveal } from "../motion/Reveal";

const commitments = [
  ["Local talent", "We intend to grow a team of skilled African engineers, technicians and developers."],
  ["Practical skills", "Our ambition includes mentorship, internships and hands-on training as the company grows."],
  ["Homegrown products", "Building products such as Brendan here, designed around African contexts."],
];

export const Vision = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["-8%", "8%"]);

  return (
    <section id="vision" ref={ref} data-testid="vision-section" className="relative overflow-hidden bg-[#0C0A09] py-24 text-stone-100 sm:py-32">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <Eyebrow>08 — Vision</Eyebrow>
        <div className="mt-10 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <h2 className="font-display text-4xl font-medium leading-[1.02] tracking-tight sm:text-6xl">
              <MaskedLines inView lines={["Built in Africa.", "Built for what", <span key="n" className="tc-copper-text">comes next.</span>]} />
            </h2>
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-lg text-base leading-relaxed text-stone-300 sm:text-lg">
                We believe African organisations deserve world-class technology delivered by people who understand local realities.
                Our long-term vision is to contribute to Africa's innovation story — creating opportunity, developing talent
                and building technology with care.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <div className="relative aspect-[5/4] overflow-hidden rounded-[28px] [clip-path:polygon(0_0,100%_0,100%_86%,86%_100%,0_100%)]">
              <motion.img style={{ y, scale: 1.18 }} src="/images/nairobi-twilight.jpg" alt="Nairobi skyline at twilight with city lights" loading="lazy"
                className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0C0A09]/80 via-transparent to-orange-600/20" />
            </div>
          </div>
        </div>
        <div className="mt-16 grid gap-4 md:grid-cols-3">
          {commitments.map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.08} className="rounded-[22px] border border-white/10 p-7">
              <p className="font-mono text-xs text-orange-400">0{i + 1}</p>
              <p className="mt-8 font-display text-xl">{t}</p>
              <p className="mt-2 text-sm leading-relaxed text-stone-400">{d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
